import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { disciplineKeys } from "@/lib/query";

export type Discipline = {
  id: string;
  code: string;
  name: string;
  color: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useDisciplines(opts: { includeInactive?: boolean } = {}) {
  return useQuery({
    queryKey: disciplineKeys.list(opts),
    queryFn: () =>
      jsonFetch<Discipline[]>(
        `/api/disciplines${opts.includeInactive ? "?includeInactive=true" : ""}`,
      ),
  });
}

export function useCreateDiscipline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; name: string; color: string }) =>
      jsonFetch<Discipline>("/api/disciplines", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: disciplineKeys.all });
      toast.success("Discipline created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateDiscipline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name?: string; active?: boolean; color?: string }) =>
      jsonFetch<Discipline>(`/api/disciplines/${input.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: input.name, active: input.active, color: input.color }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: disciplineKeys.all });
      toast.success("Discipline updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
